import React, { useEffect, useRef } from "react";
import { RouterProvider } from "react-router-dom";
import { Toaster } from "sonner";
import { Spinner } from "@/components/ui/Spinner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuthStore } from "@/stores/authStore";
import { useSystemStore } from "@/stores/systemStore";
import GlobalNetworkStatus from "@/components/global/GlobalNetworkStatus";
// Providers are now wrapped inside the router's Root element
// Layouts are now referenced inside router.tsx

// Routes
import { router } from "@/router";
import { logger } from "@/services/LoggingService";
// Providers moved under router RootProviders in router.tsx

// Create a client
const queryClient = new QueryClient();

// App initialization component
function AppInitializer({ children }: { children: React.ReactNode }) {
  const { initialize, user } = useAuthStore();
  const { testStorageCapability, detectPreviewEnvironment } = useSystemStore();
  const hasRunRef = useRef(false);
  const dbInitRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    // Initialize system capabilities
    testStorageCapability();
    detectPreviewEnvironment();
    
    // Initialize auth
    initialize();

    // Initialize multi-store (cutover: always enabled)
    setTimeout(() => {
      import('@/stores/multiWealthica2Store').then(({ useMultiWealthica2Store }) => {
        // Trigger store initialization and hydration
        useMultiWealthica2Store.getState();
        logger.info('App', '🚀 MULTI-STORE: Eager initialization completed');
        
        // Initialize from database when user is authenticated
        const authStore = useAuthStore.getState();
        if (authStore.user?.id) {
          logger.info('App', '🔄 MULTI-STORE: Initializing from database for authenticated user');
          useMultiWealthica2Store.getState().initializeFromDatabase(authStore.user.id);
        }
      }).catch(console.error);
    }, 100); // Minimal 100ms delay for platform compatibility
  }, [initialize, testStorageCapability, detectPreviewEnvironment]);

  // Listen for auth state changes to trigger database initialization
  useEffect(() => {
    if (!user?.id || dbInitRef.current) return;
    
    // Cutover: multi-store always active
    dbInitRef.current = true;
    
    setTimeout(() => {
      import('@/stores/multiWealthica2Store').then(({ useMultiWealthica2Store }) => {
        logger.info('App', '🔄 MULTI-STORE: Auth state changed - initializing from database', { userId: user.id });
        useMultiWealthica2Store.getState().initializeFromDatabase(user.id);
      }).catch(console.error);
    }, 200); // Small delay to allow auth to fully settle
  }, [user?.id]);

  return <>{children}</>;
}

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={200}>
        <AppInitializer>
          <RouterProvider router={router} future={{ v7_startTransition: true }} />
          <GlobalNetworkStatus />
          <Toaster
            position="top-right"
            icons={{
              loading: <Spinner size="sm" variant="muted" ariaLabel="Loading" />
            }}
          />
        </AppInitializer>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
